import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { SEO } from '@/components/SEO';
import Navigation from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { lovable } from '@/integrations/lovable';
import { useI18n } from '@/i18n';
import { Mail, LogOut, LayoutDashboard } from 'lucide-react';

const AgentLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signUp, signOut, user } = useAuth();
  const navigate = useNavigate();
  const { getLocalizedPath } = useI18n();
  const dashboardPath = getLocalizedPath('/agent-dashboard');

  const handleLogout = async () => {
    await signOut();
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await signIn(email, password);
    
    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        toast.error('Invalid email or password. Please check your credentials or sign up first.');
      } else {
        toast.error('Failed to sign in. Please try again.');
      }
      setLoading(false);
    } else {
      toast.success('Signed in successfully');
      setTimeout(() => {
        navigate(dashboardPath);
      }, 500);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const { error } = await signUp(email, password, `${window.location.origin}${dashboardPath}`);
    
    if (error) {
      toast.error(error.message || 'Failed to create account. Please try again.');
      setLoading(false);
    } else {
      toast.success('Account created. Please check your email to confirm, then sign in.');
      setPassword('');
      setConfirmPassword('');
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    try {
      const result = await lovable.auth.signInWithOAuth('google', {
        redirect_uri: `${window.location.origin}${dashboardPath}`,
        extraParams: {
          prompt: 'select_account'
        }
      });

      if (result.error) throw result.error;

      if (!result.redirected) {
        navigate(dashboardPath);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Google sign-in failed. Please try again.');
      console.error('Google sign-in error:', error);
    }
  };

  return (
    <>
      <SEO
        title="Agent Login | Aureum VII Trading"
        description="Access your Aureum VII Trading agent dashboard"
        type="website"
        noindex
      />
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center p-4 pt-32">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Agent Portal
            </CardTitle>
            <CardDescription className="text-base">
              {user ? `Signed in as ${user.email}` : 'Sign in to access your agent dashboard'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {user ? (
              <div className="space-y-4">
                <Button
                  className="w-full h-12 text-base font-semibold"
                  onClick={() => navigate(dashboardPath)}
                >
                  <LayoutDashboard className="mr-2 h-5 w-5" />
                  Go to Dashboard
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-12"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-5 w-5" />
                  Log out
                </Button>
              </div>
            ) : (
              <></>
            )}
            {!user && (
            <>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="agent@aureum7trading.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 text-base font-semibold" 
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="agent@aureum7trading.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-12"
                      minLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-12"
                      minLength={6}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 text-base font-semibold" 
                    disabled={loading}
                  >
                    {loading ? 'Creating account...' : 'Sign Up'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <Button
              variant="outline"
              type="button"
              className="w-full h-12"
              onClick={handleGoogleSignIn}
            >
              <Mail className="mr-2 h-5 w-5" />
              Sign in with Google
            </Button>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Need access? Contact your administrator
              </p>
            </div>
            </>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />
    </>
  );
};

export default AgentLogin;
